import { giphyLightGrey } from '@giphy/js-brand'
import React, { ReactNode, useContext, useEffect, useState } from 'react'
import { useMedia } from 'react-use'
import { LeaderBoard } from 'shared/components/leader-board'
import { getAggregations } from 'shared/components/leader-board/networking'
import { GraphAnalytics } from 'shared/components/line-chart'
import { Loader } from 'shared/components/preloader-animation'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import log from 'shared/util/log'
import { mobile } from 'shared/util/media-query'
import styled from 'styled-components'
import MoreQuestions from '../../shared/components/more-questions'
import DashboardUserContext from '../dashboard/context'
import DashboardTrendingSearches from '../../shared/components/dashboard-trending-searches'
import ContentTypePicker from './content-type-picker'
import DateRangeModule from './date-range-module'
import DisclaimerBanner from './disclaimer-banner'
import ErrorState from './error-state'
import UserAnalyticsSummary from './user-analytics-summary'

const UITray = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
`

const CustomRangeText = styled.div`
    display: flex;
    width: 100%;
    font-weight: bold;
    font-size: 14px;
    color: white;
    margin-top: 2px;
    span {
        color: ${giphyLightGrey};
        margin-right: 5px;
    }
`
const DashboardContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 10px;
    ${mobile.css`
       padding: 0 10px;
    `}
`

export const singularContentTypes = {
    'GIFs & Stickers': 'GIF & Sticker',
    GIFs: 'GIF',
    Stickers: 'Sticker',
    Stories: 'Story',
    Clips: 'Clip',
}

type GranularPermissions = { canViewContentPicker?: boolean; canViewDatePicker?: boolean; canViewInsights?: boolean }
type DatePresetType = 'WEEK' | 'MONTH' | 'ALL_TIME' | 'CUSTOM'
type Props = {
    children?: ReactNode
}

const App = ({ children }: Props) => {
    const isMobile = useMedia(mobile.query)
    const { user } = useContext(DashboardUserContext)
    const [isLoading, setIsLoading] = useState(true)
    const [totalCounts, setTotalCounts] = useState({
        uploadCount: 0,
        viewCount: 0,
    })
    const [activeErrorState, setActiveErrorState] = useState(false)
    const [contentType, setContentType] = useState('GIFs & Stickers')
    const [datePreset, setDatePreset] = useState<DatePresetType>('ALL_TIME')
    const [dateRange, setDateRange] = useState([new Date(2013, 1, 1), new Date()])
    const [granularPermissions, setGranularPermissions] = useState<GranularPermissions>({
        canViewContentPicker: false,
        canViewDatePicker: false,
    })

    const fetchUserSummary = async dateRange => {
        try {
            const { token } = user
            const { uploadCount = 0, viewCount = 0 } = await getAggregations(token, contentType, dateRange)
            setTotalCounts({ uploadCount, viewCount })
            setIsLoading(false)
        } catch (e) {
            log.error(e)
            setIsLoading(false)
            setActiveErrorState(true)
        }
    }

    useEffect(() => {
        if (user.token) {
            const {
                dashboard_analytics: canViewAnalytics,
                view_contenttype_picker_dashboard: canViewContentPicker,
                view_datetype_picker_dashboard: canViewDatePicker,
                view_top_insights_dashboard: canViewInsights,
            } = user.permissions

            // Set granular permissions for extended Analytics features
            setGranularPermissions({ canViewContentPicker, canViewDatePicker, canViewInsights })
            if (canViewAnalytics) {
                fetchUserSummary(dateRange)
            } else {
                setIsLoading(false)
            }
        }
    }, [user])

    useEffect(() => {
        if (user.token) {
            fetchUserSummary(dateRange)
        }
    }, [contentType, dateRange])

    const contentTypeUpdated = newContentType => {
        // sometimes it is possible for a user to uncheck a previous option in the select list and to have zero options selected. In this case, default back to GIFs.
        let safeNewContentType
        if (newContentType === null) {
            safeNewContentType = 'GIFs & Stickers'
        } else {
            safeNewContentType = newContentType
        }
        googleAnalyticsEvent({ category: 'Dashboard', action: 'Content Type', label: newContentType })
        setContentType(safeNewContentType)
    }

    const dateRangeUpdated = (datePreset, dateRange) => {
        // Make sure user has chosen two dates and not just one.
        if (dateRange.length === 2 && dateRange[0] != null && dateRange[1] != null && dateRange[0] !== dateRange[1]) {
            // Make sure the date range is in the right order! Sometimes a user can select a range 'backwards'
            dateRange.sort(function(a, b) {
                return new Date(a).getTime() - new Date(b).getTime()
            })
            // Make sure the 'later' date range is not after today!
            if (new Date(dateRange[1]) > new Date()) {
                dateRange[1] = new Date()
            }

            // If the earlier date is after today, just return nothing.
            if (new Date(dateRange[0]) > new Date()) {
                return
            }

            setDateRange(dateRange)
            setDatePreset(datePreset)
        }
    }

    const dateOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }

    if (isLoading) {
        return <Loader />
    }

    if (activeErrorState) {
        // An error was thrown fetching gifs
        return <ErrorState isError={true} />
    }

    const { canViewContentPicker, canViewDatePicker, canViewInsights } = granularPermissions
    const { viewCount, uploadCount } = totalCounts
    const canViewDashboardTrendingSearchesV1 = localStorage.getItem('optimizeDashboardTrendingSearchesVariantV1')
    return (
        <div>
            <DashboardContainer>
                <UITray>
                    <DateRangeModule
                        currentPreset={datePreset}
                        canViewDatePicker={canViewDatePicker}
                        onDateUpdate={dateRangeUpdated}
                    />
                    {canViewContentPicker ? <ContentTypePicker onChange={contentTypeUpdated} /> : <div />}
                </UITray>
                {datePreset === 'CUSTOM' && (
                    <CustomRangeText>
                        <span>Custom Range:</span>
                        {dateRange[0].toLocaleDateString('en-us', dateOptions)} -{' '}
                        {dateRange[1].toLocaleDateString('en-us', dateOptions)}
                    </CustomRangeText>
                )}
                <UserAnalyticsSummary viewCount={viewCount} uploadCount={uploadCount} contentType={contentType} />
                {children}
                <div>
                    <GraphAnalytics
                        contentType={contentType}
                        isMobile={isMobile}
                        user={user}
                        activeTimeSeries={datePreset}
                        dateRange={dateRange}
                    />
                    {singularContentTypes[contentType] === 'Clip' ? <DisclaimerBanner /> : null}
                    <LeaderBoard dateRange={dateRange} contentType={contentType} datePreset={datePreset} />
                </div>
                <DashboardTrendingSearches user={user} v1Variant={canViewDashboardTrendingSearchesV1} />
                {canViewInsights && (
                    <MoreQuestions
                        title={'Got More Questions?'}
                        description={
                            'For further questions, troubleshooting, or inquires about more data, visit our Support Article!'
                        }
                        helpLink={`https://support.giphy.com/hc/en-us/articles/360042381152-Analytics-Dashboard-FAQ`}
                    />
                )}
            </DashboardContainer>
        </div>
    )
}

export default App
